import React, { useEffect, useState } from "react";
import { Table } from "antd";
import FETCH from "../../../utils/fetch";
import NewLoader from "../../../components/styled-components/loader/NewLoader";
import moment from "moment";
import swal from "sweetalert";
import { ToolTipCreator } from "../../../helpers/ToolTipCreator";

export const EducationAssetAssignTable = ({
    rows,
    data,
    handleCashModuleCreate,
    cbFormChange,
    allLiabilities,
    attributeValues,
}) => {
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [cachedData, setCachedData] = useState({});
    const [assetsList, setAssetsList] = useState([]);
    const [liabilities, setLiabilities] = useState(allLiabilities);

    const [total, setTotal] = useState(null);
    const [dataSource, setDataSource] = useState(rows);
    const [count, setCount] = useState((rows && rows.length) + 1 || 1);

    const [totalAmount, setTotalAmount] = useState(0);
    const [totalAmountUsed, setTotalAmountUsed] = useState(0);

    useEffect(() => {
        setDataSource(rows);
        const amountUsed =
            rows && Array.isArray(rows)
                ? rows.reduce(
                    (acc, curr) => acc + (parseFloat(curr.amountUseForGoal) || 0),
                    0
                )
                : 0;
        const havefromFriends =
            data &&
            data["Amount Received"] &&
            (Array.isArray(data["Amount Received"])
                ? data["Amount Received"].reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0)
                : 0);
        const totalhave = (amountUsed || 0) + (havefromFriends || 0);
        setTotalAmount(Number(totalhave) || 0);
        setTotalAmountUsed(Number(amountUsed) || 0);
    }, [rows, data]);

    const handleAdd = async (record) => {
        if (dataSource && dataSource.some((item) => item.assetId === record.id)) {
            swal("Info", "This asset has already been added.", "info");
            return;
        }

        const transformedData = liabilities && calculatePayments(liabilities);
        let result = record && calculateGoalRequirements(record, data, transformedData);

        const havefromFriends =
            data &&
            data["Amount Received"] &&
            (Array.isArray(data["Amount Received"])
                ? data["Amount Received"].reduce((acc, curr) => acc + (parseFloat(curr) || 0), 0)
                : 0);

        const amountUsed =
            (rows && Array.isArray(rows)
                ? rows.reduce(
                    (acc, curr) => acc + (parseFloat(curr.amountUseForGoal) || 0),
                    0
                )
                : 0) + (parseFloat(havefromFriends) || 0);

        const goalNeedToday = result && result.goalNeedToday ? (parseFloat(result.goalNeedToday.replace(/[^0-9.-]+/g, "")) || 0) : 0;
        let totalNeed = (totalAmount || 0) + (result && result.equityForGoal ? (parseFloat(result.equityForGoal.replace(/[^0-9.-]+/g, "")) || 0) : 0);

        if (goalNeedToday > totalNeed && amountUsed < goalNeedToday) {
            let totalFundedPercentage = ((totalNeed / goalNeedToday) * 100).toFixed(2);
            swal("", `This Goal is ${totalFundedPercentage}% funded.`, "warning");
        } else {
            if (amountUsed > goalNeedToday) {
                swal("Oops", "Goal Need Already Fulfilled", "error");
                return;
            } else {
                // Surplus logic: Create Future Asset
                let surplus = totalNeed - goalNeedToday;
                if (surplus > 0) {
                    let moduleToCreate = {
                        "Name of Asset": record && record["Name of Asset"] ? "Remaining from Sale of " + record["Name of Asset"] : "Education Goal Surplus",
                        "Monetary Value": surplus,
                        "Cost Basis Date": record["Cost Basis Date"] || new Date(),
                        "Cost Basis": record["Cost Basis"] || 0,
                        "Value as of Date": data && data["Goal Date"] ? new Date(data["Goal Date"]) : new Date(),
                        "Date of Sale": data && data["Goal Date"] ? new Date(data["Goal Date"]) : new Date(),
                        future_asset: true,
                    };
                    handleCashModuleCreate(moduleToCreate);
                    swal("Success", "Goal Fully Funded! Surplus moved to Future Assets.", "success");
                } else {
                    swal("Success", "Goal Fully Funded!", "success");
                }
            }
        }

        // Illiquid assets are sold in entirety for Education
        const equityForGoalValue = parseFloat(result.equityForGoal.replace(/[^0-9.-]+/g, ""));
        const amountToUse = (goalNeedToday - amountUsed) > equityForGoalValue ? equityForGoalValue : +(goalNeedToday - amountUsed).toFixed(2);

        const newData = {
            key: count.toString(),
            assetId: record.id,
            priority: count,
            asset: record && record["Name of Asset"] ? record["Name of Asset"] : null,
            valueOfAssetGoalRate: record && record["Monetary Value"] ? record["Monetary Value"] : null,
            loanValueGoalDate: result && result.loanPayableForAsset ? parseFloat(result.loanPayableForAsset.replace(/[^0-9.-]+/g, "")) : null,
            amountUseForGoal: amountToUse,
            taxes: record && record["Taxes"] ? record["Taxes"] : null,
            netAmountAfterTax: (totalAmountUsed + amountToUse),
        };

        const updatedDataSource = [...dataSource, newData];
        setDataSource(updatedDataSource);
        setCount(count + 1);
        cbFormChange(updatedDataSource);
    };

    const handleDelete = (record) => {
        const matchedData = dataSource && dataSource.find((item) => item.assetId === record.id);
        if (matchedData) {
            const amountToUse = parseFloat(matchedData.amountUseForGoal) || 0;
            const updatedTotalAmountUsed = totalAmountUsed - amountToUse;

            const newData = dataSource
                .filter((item) => item.assetId !== record.id)
                .map((item) => ({
                    ...item,
                    netAmountAfterTax: updatedTotalAmountUsed,
                }));

            setDataSource(newData);
            setTotalAmountUsed(updatedTotalAmountUsed);
            setCount(count - 1);
            cbFormChange(newData);
        }
    };

    const getAssetsList = async (page) => {
        if (cachedData && cachedData[page]) {
            updateStateWithData(cachedData[page]);
            return;
        }
        try {
            setLoading(true);
            const userRole = localStorage.getItem("role") !== "undefined" && JSON.parse(localStorage.getItem("role"));
            const id = JSON.parse(localStorage.getItem("accessId"));
            let datares = await FETCH.post({
                url: "client-module-service/client-module/illiquid",
                id: userRole === "Financial Advisor Client" ? `?page=${page}&limit=10&clientId=${id}` : `?page=${page}&limit=10`,
                body: {},
            });
            setLoading(false);

            if (datares && datares.records) {
                setCachedData((prevState) => ({
                    ...prevState,
                    [page]: datares,
                }));
                updateStateWithData(datares);
            }
        } catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    const updateStateWithData = (datares) => {
        if (datares && datares.records) {
            const filteredAssets = datares.records.filter(
                (record) => !record.hasOwnProperty("This Asset is Liquid") || record["This Asset is Liquid"] === false
            );
            const assetsWithMonetaryValue = filteredAssets.map((asset) => ({
                ...asset,
                "Monetary Value": +calculateMonetoryValue(asset),
            }));
            setAssetsList(assetsWithMonetaryValue);
        }
        if (datares && datares.pagination) {
            setTotal(datares.pagination.total);
            setTotalPages(Math.ceil(datares.pagination.total / datares.pagination.limit));
        }
    };

    const calculateMonetoryValue = (asset) => {
        const currentDate = data["Goal Date"] ? new Date(data["Goal Date"]) : new Date();
        let Start = (asset["Cost Basis Date"] && new Date(asset["Cost Basis Date"])) || new Date();
        const differenceInMs = currentDate.getTime() - Start.getTime();
        const daysDifference = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
        let totalDays = daysDifference / 365;

        let Cost = asset["Cost Basis"]
            ? (typeof asset["Cost Basis"] === "string" ? (parseFloat(asset["Cost Basis"].replace(/[^0-9.-]+/g, "")) || 0) : (+asset["Cost Basis"] || 0))
            : 0;
        let ReturnRate = asset["Static"]
            ? (typeof asset["Static"] === "string" ? (parseFloat(asset["Static"].replace(/[^0-9.-]+/g, "")) || 0) : (+asset["Static"] || 0))
            : 0;
        let decimalValue = ReturnRate ? ReturnRate / 100 : 0;
        const amount = 1 + decimalValue / 365;
        let power = Math.pow(amount, totalDays * 365);
        const estimateDiff = Cost * power;

        return estimateDiff > 0 && !isNaN(estimateDiff) ? parseFloat(estimateDiff).toFixed(2) : 0;
    };

    const calculatePayments = (liabilities) => {
        if (!liabilities) return {};
        const result = {};
        liabilities.forEach((item) => {
            const nickname = item["Nickname of Liabilities"];
            let monthlyPayment = parseFloat(String(item["Monthly Payment"] || 0).replace(/[$,]/g, "")) || 0;
            let initialLoanAmount = parseFloat(String(item["Initial Loan Amount"] || 0).replace(/[$,]/g, "")) || 0;
            let startDate = new Date(item["Start Date"] || new Date());
            let endDate = new Date(item["Maturity Date"] || new Date());
            let interestRate = parseFloat(String(item["Interest Rate"] || 0).replace(/[%]/g, "")) / 100 || 0;

            const payments = [];
            let currentDate = new Date(startDate);
            const monthlyInterestRate = interestRate / 12;
            let remainingBalance = initialLoanAmount;

            let iteration = 0;
            while (currentDate <= endDate && remainingBalance > 0 && iteration < 1200) {
                const interestPaid = remainingBalance * monthlyInterestRate;
                const principalPaid = Math.min(monthlyPayment - interestPaid, remainingBalance);
                const valuePayable = Math.round((interestPaid + principalPaid) * 100) / 100;
                if (valuePayable > 0) {
                    payments.push({ date: moment(currentDate).format("MM-DD-YYYY"), valuepayable: valuePayable });
                }
                remainingBalance -= principalPaid;
                currentDate.setMonth(currentDate.getMonth() + 1);
                iteration++;
            }
            result[nickname] = payments;
        });
        return result;
    };

    const calculateGoalRequirements = (record, data, amortization) => {
        const assetName = record["Name of Asset"];
        const amortizationofRecord = amortization && amortization[assetName];
        const todaysDate = new Date();
        const GoalDate = data["Goal Date"] ? new Date(data["Goal Date"]) : new Date();

        const loanPaybaleForAsset = amortizationofRecord
            ? amortizationofRecord
                .filter((r) => new Date(r.date) >= GoalDate)
                .reduce((total, r) => total + (typeof r.valuepayable === "number" ? r.valuepayable : 0), 0)
            : 0;

        const assetValueToday = record["Monetary Value"]
            ? (typeof record["Monetary Value"] === "string" ? (parseFloat(record["Monetary Value"].replace(/[^0-9.-]+/g, "")) || 0) : (+record["Monetary Value"] || 0))
            : 0;
        const taxabilityonAsset = record["Taxes"]
            ? (typeof record["Taxes"] === "string" ? (parseFloat(record["Taxes"].replace(/[^0-9.-]+/g, "")) || 0) : (+record["Taxes"] || 0))
            : 0;
        const costToSell = record["Commission on Sale"]
            ? (typeof record["Commission on Sale"] === "string" ? (parseFloat(record["Commission on Sale"].replace(/[^0-9.-]+/g, "")) || 0) : (+record["Commission on Sale"] || 0))
            : 0;
        const liabilityPayOff = record["Total Liabilities"]
            ? (typeof record["Total Liabilities"] === "string" ? (parseFloat(record["Total Liabilities"].replace(/[^0-9.-]+/g, "")) || 0) : (+record["Total Liabilities"] || 0))
            : 0;
        const transactionCost = record["Other Transaction Costs"]
            ? (typeof record["Other Transaction Costs"] === "string" ? (parseFloat(record["Other Transaction Costs"].replace(/[^0-9.-]+/g, "")) || 0) : (+record["Other Transaction Costs"] || 0))
            : 0;

        // Net Proceeds = Value at Goal Date – Selling Costs – Estimated Taxes – Total Liabilities
        const equityForGoal = (
            (assetValueToday || 0) -
            (taxabilityonAsset || 0) -
            (costToSell || 0) -
            (liabilityPayOff || 0) -
            (transactionCost || 0) -
            (loanPaybaleForAsset || 0)
        ).toFixed(2);

        const GoalNeedToday = data["Dollar Amount Needed"]
            ? (typeof data["Dollar Amount Needed"] === "string" ? (parseFloat(data["Dollar Amount Needed"].replace(/[^0-9.-]+/g, "")) || 0) : (+data["Dollar Amount Needed"] || 0))
            : 0;
        const GoalInflationRate = 0.05; // Default 5% for Education

        const years = (GoalDate - todaysDate) / (1000 * 60 * 60 * 24 * 365);
        const result = (GoalNeedToday || 0) * Math.pow(1 + GoalInflationRate, (years || 0));

        return {
            equityForGoal: `$${parseFloat(equityForGoal).toLocaleString()}`,
            goalNeedToday: `$${result.toLocaleString()}`,
            loanPayableForAsset: `$${loanPaybaleForAsset.toLocaleString()}`,
        };
    };

    useEffect(() => {
        getAssetsList(currentPage);
    }, [currentPage]);

    const colsNonLiquidAssets = [
        {
            title: "Asset",
            dataIndex: "Name of Asset",
            render: (record) => record || "--------",
        },
        {
            title: "Net Proceeds (Goal Date)",
            render: (fileName, record) => {
                const transformedData = liabilities && calculatePayments(liabilities);
                let result = record && calculateGoalRequirements(record, data, transformedData);
                return result ? <div>{result.equityForGoal}</div> : "N/A";
            },
        },
        {
            title: "Value on Goal Date",
            dataIndex: "Monetary Value",
            render: (record) => record ? `$${parseFloat(record).toLocaleString()}` : "N/A",
        },
        {
            title: "Selling Costs",
            dataIndex: "Commission on Sale",
            render: (record) => record ? `$${parseFloat(record).toLocaleString()}` : "N/A",
        },
        {
            title: "Taxes",
            dataIndex: "Taxes",
            render: (record) => record ? `$${parseFloat(record).toLocaleString()}` : "N/A",
        },
        {
            title: "Liabilities",
            render: (fileName, record) => {
                const transformedData = liabilities && calculatePayments(liabilities);
                let result = record && calculateGoalRequirements(record, data, transformedData);
                return result ? <div>{result.loanPayableForAsset}</div> : "N/A";
            },
        },
        {
            title: "ACTION",
            render: (record) => {
                let isSelected = dataSource && dataSource.some((item) => item.assetId === record.id);
                return (
                    <button
                        className="saveStatusbutton"
                        style={{
                            width: "100%",
                            fontWeight: "bold",
                            background: isSelected ? "#E13C25" : "",
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            isSelected ? handleDelete(record) : handleAdd(record);
                        }}
                    >
                        {isSelected ? "Selected" : "Select"}
                    </button>
                );
            },
        },
    ];

    return (
        <div className="assetAssignTable" style={{ position: "relative" }}>
            <div className="goalSubFormTable">
                <div style={{ display: "flex", alignItems: "baseline", columnGap: "3px" }}>
                    <h3 className="incomecardheading">Education Goal: Illiquid Assets to be Sold</h3>
                    <ToolTipCreator name={"Goals_Illiquid_Assets"} />
                </div>
                <Table
                    columns={colsNonLiquidAssets}
                    dataSource={assetsList}
                    pagination={{
                        total: totalPages * 10,
                        current: currentPage,
                        onChange: (page) => setCurrentPage(page),
                        pageSize: 10,
                    }}
                />
            </div>
            <NewLoader isLoading={loading} />
        </div>
    );
};
